import { useEffect, useMemo, useState } from "react";
import { useGetAssetsQuery } from "../redux/api";
import type { Asset } from "../redux/api";
import { Modal } from "./Modal";
import { IconCheck, IconImage, IconVideo } from "./icons";

export type GalleryKind = "image" | "video" | "all";

interface GalleryPickerModalProps {
  open: boolean;
  onClose: () => void;
  kind: GalleryKind;
  /** Allow picking more than one asset. Defaults to single-select. */
  multiple?: boolean;
  /** Only relevant when `multiple` — caps how many can be selected at once. */
  maxSelect?: number;
  /** Keys already attached to the record you're picking for — shown pre-checked
   * and locked (can't be toggled off here; remove them from the record itself
   * instead) so it's clear what's already added and you don't re-add a duplicate. */
  alreadySelectedKeys?: string[];
  onSelect: (assets: Asset[]) => void;
}

const isVideoAsset = (a: Asset) =>
  a.mimeType.startsWith("video/") || a.mimeType === "application/vnd.apple.mpegurl";

const matchesKind = (a: Asset, kind: GalleryKind) => {
  if (kind === "all") return true;
  if (kind === "video") return isVideoAsset(a);
  return !isVideoAsset(a);
};

const formatSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

// Shared "choose from gallery" picker used by Product/Brand/Organization image
// uploads — same visual grid as the Gallery screen itself (see
// screens/settings/gallery.tsx), just wrapped in a Modal with a select/cancel
// footer instead of a delete button per item.
export const GalleryPickerModal = ({
  open,
  onClose,
  kind,
  multiple,
  maxSelect,
  alreadySelectedKeys = [],
  onSelect,
}: GalleryPickerModalProps) => {
  const { data: assets = [], isLoading } = useGetAssetsQuery();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const items = useMemo(() => assets.filter((a) => matchesKind(a, kind)), [assets, kind]);

  const alreadyAttachedIds = useMemo(
    () => new Set(items.filter((a) => alreadySelectedKeys.includes(a.key)).map((a) => a.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, alreadySelectedKeys.join(",")],
  );

  useEffect(() => {
    if (open) setSelectedIds(Array.from(alreadyAttachedIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const limit = multiple ? maxSelect ?? Infinity : 1;
  const newSelectedCount = selectedIds.filter((id) => !alreadyAttachedIds.has(id)).length;

  const toggle = (asset: Asset) => {
    if (alreadyAttachedIds.has(asset.id)) return;
    setSelectedIds((prev) => {
      if (!multiple) return prev[0] === asset.id ? [] : [asset.id];
      if (prev.includes(asset.id)) return prev.filter((id) => id !== asset.id);
      const newCount = prev.filter((id) => !alreadyAttachedIds.has(id)).length;
      if (newCount >= limit) return prev;
      return [...prev, asset.id];
    });
  };

  const confirm = () => {
    const picked = items.filter((a) => selectedIds.includes(a.id) && !alreadyAttachedIds.has(a.id));
    if (picked.length === 0) {
      onClose();
      return;
    }
    onSelect(picked);
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Choose from gallery"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={confirm}
            disabled={newSelectedCount === 0}
          >
            {`Select${newSelectedCount ? ` (${newSelectedCount})` : ""}`}
          </button>
        </>
      }
    >
      {isLoading ? (
        <p className="panel__sub">Loading…</p>
      ) : items.length === 0 ? (
        <div className="gallery-empty">Nothing in the gallery yet — upload a file first.</div>
      ) : (
        <div className="gallery-grid">
          {items.map((asset) => {
            const attached = alreadyAttachedIds.has(asset.id);
            const selected = selectedIds.includes(asset.id);
            return (
              <div
                key={asset.id}
                className={`gallery-item${attached ? " gallery-item--attached" : " gallery-item--selectable"}${selected ? " gallery-item--selected" : ""}`}
                onClick={() => toggle(asset)}
                title={attached ? "Already added" : undefined}
              >
                <div className="gallery-item__thumb">
                  {isVideoAsset(asset) ? (
                    <IconVideo size={28} />
                  ) : asset.url ? (
                    <img src={asset.url} alt={asset.fileName} />
                  ) : (
                    <IconImage size={28} />
                  )}
                </div>
                <div className="gallery-item__meta">
                  <span className="gallery-item__name" title={asset.fileName}>
                    {asset.fileName}
                  </span>
                  <span className="gallery-item__sub">{formatSize(asset.fileSize)}</span>
                </div>
                <span className="gallery-item__check">
                  <IconCheck size={14} />
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};
